import { useState, FormEvent, ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/axios";

interface LoginResponse {
  message: string;
  token?: string;
  requires2FA?: boolean;
  tempToken?: string;
  twoFactorEnabled?: boolean;
}

const Login = () => {
  const [step, setStep] = useState<1 | 2>(1);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    twoFactorCode: "",
  });
  const [tempToken, setTempToken] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setMessage("");
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");

    try {
      if (step === 1) {
        const response = await api.post<LoginResponse>("/login/verify", {
          email: formData.email,
          password: formData.password,
        });

        if (response.data.requires2FA) {
          setTempToken(response.data.tempToken!);
          setStep(2);
        } else if (!response.data.twoFactorEnabled && response.data.tempToken) {
          localStorage.setItem("token", response.data.tempToken);
          navigate("/enable-2fa");
        } else if (response.data.token) {
          localStorage.setItem("token", response.data.token);
          navigate("/admin/dashboard");
        }
      } else {
        const response = await api.post<LoginResponse>("/login/2fa", {
          twoFactorCode: formData.twoFactorCode,
          tempToken: tempToken,
        });

        if (response.data.token) {
          localStorage.setItem("token", response.data.token);
          navigate("/admin/dashboard");
        }
      }
    } catch (error: any) {
      setMessage(error.response?.data?.message || "Login failed");
      if (step === 2) {
        setFormData((prev) => ({
          ...prev,
          twoFactorCode: "",
        }));
      } else {
        setFormData({
          email: "",
          password: "",
          twoFactorCode: "",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setTempToken("");
    setFormData((prev) => ({
      ...prev,
      twoFactorCode: "",
    }));
    setMessage("");
  };

  return (
    <div className="min-h-screen flex items-center w-full justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-md">
        <div>
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            {step === 1
              ? "Sign in to your account"
              : "Two-Factor Authentication"}
          </h2>
          {step === 2 && (
            <p className="mt-2 text-center text-sm text-gray-600">
              Enter the 6-digit code from your authenticator app
            </p>
          )}
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {step === 1 ? (
              <>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 
                             focus:outline-none focus:ring-green-500 focus:border-green-500"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 
                             focus:outline-none focus:ring-green-500 focus:border-green-500"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleInputChange}
                  />
                </div>
              </>
            ) : (
              <div>
                <label
                  htmlFor="twoFactorCode"
                  className="block text-sm font-medium text-gray-700"
                >
                  2FA Code
                </label>
                <input
                  id="twoFactorCode"
                  name="twoFactorCode"
                  type="text"
                  required
                  maxLength={6}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 
                           focus:outline-none focus:ring-green-500 focus:border-green-500"
                  placeholder="Enter 6-digit code"
                  value={formData.twoFactorCode}
                  onChange={handleInputChange}
                />
              </div>
            )}
          </div>

          {message && (
            <div className="text-red-500 text-sm text-center">{message}</div>
          )}

          <div className="flex flex-col space-y-3">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium 
                       text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 
                       focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading
                ? "Loading..."
                : step === 1
                ? "Sign in"
                : "Verify Code"}
            </button>

            {step === 2 && (
              <button
                type="button"
                onClick={handleBack}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium 
                         text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 
                         focus:ring-green-500"
              >
                Back to Login
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
